// tabs/PlayersTab.jsx
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import PlayerFormPopup from "../../../components/PlayerFormPopup";
import BulkPlayerUpload from "../../../components/BulkPlayerUpload";
import ConfirmationModal from "../../../utils/general/confirmationModal";
import { Edit2, Trash2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../components/ToastNotification";
import playericon from "../../../assets/user.webp"; // ícono por defecto
import UserPlayersAssignModal from "../../../components/UserPlayersAssignModal";

const PlayersTab = ({ team, selectedSubcategory }) => {
  const token = useSelector((state) => state.user.token);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showBulkPopup, setShowBulkPopup] = useState(false);
  const [userId, setUserId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const isOwner = userId === team.user_id; // o team.owner_id según tu backend

  const navigate = useNavigate();

  useEffect(() => {
    fetchPlayers();
  }, [team.id, token, selectedSubcategory.id]);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const fetchPlayers = async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/players/team/${team.id}`;
      if (selectedSubcategory) {
        url += `?category=${selectedSubcategory.id}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Error al obtener los jugadores.");

      const data = await response.json();
      setPlayers(data);
    } catch (error) {
      setError("Error al obtener los jugadores del equipo.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, [team.id, token, selectedSubcategory]);

  const handleDeleteClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/players/team/remove`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            team_id: team.id, // Debes tener esto definido
            player_id: selectedPlayerId,
            ...(selectedSubcategory?.id && {
              team_category_id: selectedSubcategory.id,
            }),
          }),
        },
      );

      if (!response.ok) throw new Error("Error al eliminar el jugador.");

      await fetchPlayers();
      showToast.success("Jugador eliminado correctamente.");
    } catch (error) {
      showToast.error(error.message || "Hubo un error al eliminar el jugador.");
    } finally {
      setIsModalOpen(false);
      setSelectedPlayerId(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsModalOpen(false);
    setSelectedPlayerId(null);
  };

  if (loading) return <p>Cargando jugadores...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="bg-gray-50 p-4 rounded-3xl">
      <h2 className="text-lg font-bold mb-4 text-gray-800">
        Jugadores del Equipo
      </h2>

      {/* --- 4. Contenedor de botones --- */}
      {isOwner && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setShowPopup(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-2xl font-semibold hover:bg-blue-600 transition"
          >
            Crear Jugador
          </button>
          <button
            onClick={() => setShowBulkPopup(true)}
            className="bg-green-500 text-white px-4 py-2 rounded-2xl font-semibold hover:bg-green-600 transition flex items-center gap-2"
          >
            <Upload size={18} />
            Carga Masiva
          </button>
          <button
            onClick={() => setShowAssignPopup(true)}
            className="bg-purple-500 text-white px-4 py-2 rounded-2xl font-semibold hover:bg-purple-600 transition"
          >
            Agregar desde mis jugadores
          </button>
        </div>
      )}

      {players.length === 0 ? (
        <p className="text-gray-500 text-center py-6">
          No hay jugadores en este equipo.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {players.map((player) => (
            <div
              key={player.id}
              onClick={() => navigate(`/players/${player.id}`)}
              className="relative group flex flex-col items-center gap-2 p-2 bg-white rounded-xl shadow-sm hover:shadow-md transition cursor-pointer"
            >
              {/* Botones de editar y eliminar (solo si es el creador) */}
              {userId === player.user_id && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // evita que dispare el navigate
                      setShowPopup(true); // abrir popup en modo edición
                    }}
                    className="absolute top-2 right-8 text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(player.id);
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 size={18} />
                  </button>
                </>
              )}

              <img
                src={player.profile_picture_url || playericon}
                alt={`${player.first_name} ${player.last_name}`}
                className="w-16 h-16 rounded-full object-cover"
              />
              <span className="text-gray-800 font-semibold text-center text-sm">
                {player.first_name} {player.last_name}
              </span>
              <span className="text-gray-500 text-xs">
                {player.player_type}
              </span>
            </div>
          ))}
        </div>
      )}

      {showPopup && (
        <PlayerFormPopup
          team={team}
          selectedSubcategory={selectedSubcategory}
          onClose={() => setShowPopup(false)}
          onSubmit={fetchPlayers} // refrescar lista
        />
      )}

      {showBulkPopup && (
        <BulkPlayerUpload
          team={team}
          selectedSubcategory={selectedSubcategory}
          onClose={() => setShowBulkPopup(false)}
          onUploadSuccess={() => {
            setShowBulkPopup(false);
            fetchPlayers(); // Refrescar la lista de jugadores
          }}
        />
      )}
      {showAssignPopup && (
        <UserPlayersAssignModal
          team={team}
          selectedSubcategory={selectedSubcategory}
          onClose={() => setShowAssignPopup(false)}
          onAssigned={fetchPlayers}
        />
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        message="¿Estás seguro de que deseas eliminar este jugador?"
      />
    </div>
  );
};

export default PlayersTab;
